"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.bulkUsersSchema = exports.BULK_USER_ACTION_LABELS = exports.BULK_USER_ACTIONS = exports.userSelectionSchema = exports.INVITE_MANY_OUTCOMES = exports.inviteManySchema = exports.INVITE_MANY_MAX = exports.HANDOVER_KIND_LABELS = exports.HANDOVER_KINDS = exports.suspendUserSchema = exports.DEFAULT_USER_LIST_SORT = exports.USER_LIST_SORT_COLUMNS = void 0;
exports.parseUserListSort = parseUserListSort;
exports.userListSortToString = userListSortToString;
exports.handoverPlan = handoverPlan;
exports.nameFromEmail = nameFromEmail;
const zod_1 = require("zod");
const auth_schemas_1 = require("./auth-schemas");
const pipeline_1 = require("./pipeline");
const schemas_1 = require("./schemas");
// People (USER-01/02/03), Admin redesign 2026-11-02: sort, status tiles, inviting several
// addresses at once, bulk actions on ticked people, the split hand-over and suspend.
// The list's filter schema itself lives with the other user schemas in `auth-schemas.ts`.
// --- Sort -------------------------------------------------------------------
exports.USER_LIST_SORT_COLUMNS = [
    'fullName',
    'email',
    'roleLabels',
    'ventureCodes',
    'status',
    'isPanelEligible',
    'lastLoginAt',
];
exports.DEFAULT_USER_LIST_SORT = [{ column: 'fullName', direction: 'asc' }];
/** `fullName:asc,lastLoginAt:desc`. Unknown columns are dropped; no direction means ascending. */
function parseUserListSort(value) {
    const seen = new Set();
    const parsed = (value ?? '')
        .split(',')
        .map((part) => part.trim())
        .filter(Boolean)
        .flatMap((part) => {
        const [column, direction] = part.split(':');
        if (column === undefined || !isSortColumn(column) || seen.has(column))
            return [];
        seen.add(column);
        return [{ column, direction: direction === 'desc' ? 'desc' : 'asc' }];
    });
    return parsed.length > 0 ? parsed : exports.DEFAULT_USER_LIST_SORT;
}
function userListSortToString(sort) {
    return sort.map((spec) => `${spec.column}:${spec.direction}`).join(',');
}
function isSortColumn(value) {
    return exports.USER_LIST_SORT_COLUMNS.includes(value);
}
// --- Suspend ----------------------------------------------------------------
exports.suspendUserSchema = zod_1.z.object({
    reason: zod_1.z.string().trim().max(200).optional(),
});
// --- Split hand-over (F2.05 / F2.07) ---------------------------------------
/** The kinds of work `reassignOwnedRecords` moves, in the order the hand-over asks about them. */
exports.HANDOVER_KINDS = ['requisitions', 'jobs', 'lineManagedJobs', 'tasks'];
exports.HANDOVER_KIND_LABELS = {
    // Only the requests they are recruiter on move; a request they raised stays raised by them (review R4).
    requisitions: 'Hire requests as recruiter',
    jobs: 'Jobs as recruiter',
    lineManagedJobs: 'Jobs as line manager',
    tasks: 'Tasks',
};
/**
 * Who takes each kind of work this person still has.
 *
 * A person named for a kind wins; anything else goes to the one person for everything. Kinds
 * they have none of are left out, and kinds with work but nobody named come back as `missing`.
 */
function handoverPlan(owned, choice) {
    const targets = {};
    const missing = [];
    for (const kind of exports.HANDOVER_KINDS) {
        if (owned[kind] <= 0)
            continue;
        const to = choice.handover?.[kind] ?? choice.reassignToUserId;
        if (to)
            targets[kind] = to;
        else
            missing.push(kind);
    }
    return { targets, missing };
}
// --- Invite several addresses at once (F2.01) ------------------------------
exports.INVITE_MANY_MAX = 50;
/** One role, reach and cover for all of them; each address becomes its own invite. */
exports.inviteManySchema = zod_1.z.object({
    // Checked one by one in the service, so a typo is reported against its address rather than
    // refusing the other forty-nine.
    emails: zod_1.z
        .array(zod_1.z.string().trim().max(320))
        .min(1, 'Enter at least one email address.')
        .max(exports.INVITE_MANY_MAX, `Invite ${exports.INVITE_MANY_MAX} or fewer at a time.`),
    assignment: auth_schemas_1.roleAssignmentSchema,
    managerUserId: schemas_1.uuidSchema.optional(),
    isPanelEligible: zod_1.z.boolean().default(false),
});
exports.INVITE_MANY_OUTCOMES = [
    'invited',
    'invalid',
    'duplicate',
    'already_user',
    'already_invited',
    'failed',
];
/**
 * A starting name for an address invited without one: `priya.das@…` is "Priya Das".
 * They can correct it when they accept the invite (`acceptInviteSchema.fullName`).
 */
function nameFromEmail(email) {
    const local = email.split('@')[0] ?? '';
    const name = local
        .split(/[._+-]+/)
        .map((word) => word.replace(/\d+/g, ''))
        .filter(Boolean)
        .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
        .join(' ');
    return name.length >= 2 ? name : local || email;
}
// --- Bulk actions on ticked people ------------------------------------------
/** The ids ticked, or "all N matching" as the filter plus the rows unticked since (06-qol §3). */
exports.userSelectionSchema = zod_1.z.discriminatedUnion('kind', [
    zod_1.z.object({
        kind: zod_1.z.literal('ids'),
        ids: zod_1.z.array(schemas_1.uuidSchema).min(1, 'Select at least one.').max(pipeline_1.BULK_MAX_RECORDS, pipeline_1.BULK_TOO_MANY),
    }),
    zod_1.z.object({
        kind: zod_1.z.literal('all-matching'),
        filter: auth_schemas_1.userListFilterSchema,
        excluded: zod_1.z.array(schemas_1.uuidSchema).max(1000).default([]),
    }),
]);
exports.BULK_USER_ACTIONS = [
    'set_role',
    'add_to_panel',
    'remove_from_panel',
    'resend_invite',
    'deactivate',
];
exports.BULK_USER_ACTION_LABELS = {
    set_role: 'Change role',
    add_to_panel: 'Add to interview panels',
    remove_from_panel: 'Remove from interview panels',
    resend_invite: 'Resend invite',
    deactivate: 'Deactivate',
};
exports.bulkUsersSchema = zod_1.z.discriminatedUnion('action', [
    /** Replaces each person's roles with this one role and reach. */
    zod_1.z.object({ action: zod_1.z.literal('set_role'), selection: exports.userSelectionSchema, assignment: auth_schemas_1.roleAssignmentSchema }),
    zod_1.z.object({ action: zod_1.z.literal('add_to_panel'), selection: exports.userSelectionSchema }),
    zod_1.z.object({ action: zod_1.z.literal('remove_from_panel'), selection: exports.userSelectionSchema }),
    zod_1.z.object({ action: zod_1.z.literal('resend_invite'), selection: exports.userSelectionSchema }),
    /** One person takes over everybody's work. */
    zod_1.z.object({
        action: zod_1.z.literal('deactivate'),
        selection: exports.userSelectionSchema,
        reassignToUserId: schemas_1.uuidSchema.optional(),
        reason: zod_1.z.string().trim().max(200).optional(),
    }),
]);
//# sourceMappingURL=people.js.map